import {useState, useEffect, useMemo} from 'react';
import {useSharedValue} from '@epic-core/hooks';
import {AppSharedKeys} from './sharedKeys';
import {cmsApi} from './cms.api';
import {PageData, CmsData, CmsUrlLoadingState, CmsTreeData} from '../types/cms.types';
import {useCmsFetch} from './cms.hooks';
import {isError} from '@epic-mw/error-types';

const defaultLoadingState = {loading: false, loaded: false, error: '', emptyPageData: true};

export const useCmsTreeData = <P extends PageData>(cmsUrlPattern: string): CmsTreeData<P> => {
    const [urlLoadingState, setUrlLoadingState] = useState<CmsUrlLoadingState>({
        [cmsUrlPattern]: defaultLoadingState
    });
    const loadingState = urlLoadingState[cmsUrlPattern] || defaultLoadingState;
    const [cmsStore, setCmsStore] = useSharedValue(AppSharedKeys.CmsTreeStore);
    const pageData = cmsStore[cmsUrlPattern] || {};
    const {children, treeRoot} = pageData;

    const valid = treeRoot && children && children.length !== 0;

    useEffect(() => {
        if (
            (treeRoot && valid && !loadingState.error) ||
            loadingState.loading ||
            loadingState.loaded
        ) {
            return;
        }
        async function fetchData() {
            try {
                setUrlLoadingState({[cmsUrlPattern]: {loading: true, loaded: false}});
                const fetchedData = (await cmsApi.getTreeData(cmsUrlPattern)) || {};
                const emptyPageData =
                    !fetchedData || !fetchedData.children || !fetchedData.children.length;
                setUrlLoadingState({
                    [cmsUrlPattern]: {loading: false, loaded: true, emptyPageData}
                });
                setCmsStore(
                    Object.assign({}, cmsStore, {
                        [cmsUrlPattern]: fetchedData
                    })
                );
            } catch (ex) {
                console.error('Failed to fetch cms data for page', cmsUrlPattern, ex);
                const error: string = (isError(ex) && ex.message) || '';
                setUrlLoadingState({
                    [cmsUrlPattern]: {loading: false, loaded: true, error}
                });
            }
        }
        fetchData();
    }, [treeRoot, valid, loadingState.loading, loadingState.loaded]);

    return useMemo(() => {
        return {
            children,
            treeRoot,
            cmsLoading: loadingState.loading || (!treeRoot && !loadingState.loaded),
            cmsLoadedEmpty: loadingState.loaded && loadingState.emptyPageData,
            loaded: loadingState.loaded,
            error: loadingState.error
        };
    }, [treeRoot, loadingState.loading, loadingState.loaded]);
};

/**
 * Will look at the tree store and then default to cms data
 * @param cmsUrlPattern
 */
export const useTreeDataCmsData = <P extends PageData>(
    rootUrlPattern: string,
    title: string
): CmsData<P> => {
    const pageSlug = `${rootUrlPattern}/${title}`;
    const [treeStore] = useSharedValue(AppSharedKeys.CmsTreeStore);
    const [cmsStore] = useSharedValue(AppSharedKeys.CmsStore);
    let pageData = cmsStore[pageSlug];
    if (!pageData) {
        const rootData = treeStore[rootUrlPattern] || {};
        const {children = []} = rootData;
        children.forEach((child) => {
            if (child._slug === title) {
                pageData = child;
            }
        });
    }

    return useCmsFetch(pageSlug, pageData);
};
